import type { ReactNode } from "react"

import { clsx } from "clsx"

import type {
  SolarFinancingSummaryDto,
  SolarTpoTermsDto,
} from "~/api/dto/solar-proposal-pricing.dto"
import {
  LIGHTREACH_TPO_FINAL_TERMS,
  lightReachTpoPaymentFinePrint,
  TPO_DISCLOSURES,
  TPO_DISCLOSURES_HEADING,
  TPO_DISCLOSURES_INTRO,
  TPO_FAQ,
  TPO_FAQ_HEADING,
  TPO_FAQ_LEASE_AND_PPA_LABEL,
  TPO_FAQ_LOAN_LABEL,
} from "~/utils/finance/tpo-disclosures"

import {
  formatTpoEscalator,
  TPO_AGREEMENT_LABEL,
  TPO_ESCALATOR_LABEL,
  TPO_FIRST_YEAR_PAYMENT_LABEL,
  TPO_KWH_RATE_LABEL,
  TPO_PAYMENT_OUT_OF_DATE_LABEL,
  TPO_PAYMENT_UNAVAILABLE_LABEL,
  TPO_PROVIDER_LABEL,
  TPO_TERM_LABEL,
  tpoAgreementText,
  tpoTermText,
} from "./solar-tpo-copy"
import { Eyebrow, Panel } from "../BatteryOnlyProposal/primitives"
import {
  formatUsdExact,
  formatUsdPerKwh,
} from "../BatteryOnlyProposal/view-helpers"
import { OutOfDateIndicator } from "../ProposalPresentation/pricing/pricing-out-of-date"
import {
  STALE_FIGURES_NOTE,
  STALE_FIGURES_WITHIN_CLASS,
} from "../ProposalPresentation/updates/stale-figure"

function TpoStat({
  label,
  value,
  testId,
  children,
}: {
  label: string
  value: string
  testId: string
  children?: ReactNode
}): JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <dt>
        <Eyebrow>{label}</Eyebrow>
      </dt>
      <dd
        data-testid={testId}
        className="font-display flex flex-wrap items-center gap-2 text-[length:var(--step-1)] font-semibold tracking-[-0.02em] text-bp-text tabular-nums"
      >
        {value}
        {children}
      </dd>
    </div>
  )
}

/**
 * A TPO's terms as the homeowner reads them: LightReach's first-year monthly
 * payment, its $/kWh for a PPA, the annual escalator, the term and who
 * provides it, then V1's TPO disclosures and FAQ. Never a system price, net cost,
 * financed amount or APR. A payment that isn't current stays on the seller's
 * screen, marked, but never prints.
 */
export function SolarTpoCard({
  terms,
  financingSummary,
  outOfDate,
  marked,
  action,
}: {
  terms: SolarTpoTermsDto
  financingSummary: SolarFinancingSummaryDto
  outOfDate: boolean
  marked: boolean
  action: ReactNode
}): JSX.Element {
  const current = financingSummary.status === `current` && !outOfDate
  const payment = financingSummary.estimatedMonthlyPayment
  return (
    <Panel
      className={clsx(
        `flex flex-col gap-6 px-6 py-7 lg:px-8`,
        !current && `print:hidden`,
      )}
    >
      <div
        data-testid="solar-tpo-terms"
        className={marked ? STALE_FIGURES_WITHIN_CLASS : undefined}
      >
        {marked ? <p className="sr-only">{STALE_FIGURES_NOTE}</p> : null}
        <dl className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          <TpoStat
            label={TPO_FIRST_YEAR_PAYMENT_LABEL}
            value={
              payment != null
                ? `${formatUsdExact(payment)}/mo`
                : TPO_PAYMENT_UNAVAILABLE_LABEL
            }
            testId="solar-tpo-first-year-payment"
          >
            {financingSummary.status === `stale` ? (
              <OutOfDateIndicator label={TPO_PAYMENT_OUT_OF_DATE_LABEL} />
            ) : null}
          </TpoStat>
          {terms.kind === `ppa` && terms.kwhRate != null ? (
            <TpoStat
              label={TPO_KWH_RATE_LABEL}
              value={formatUsdPerKwh(terms.kwhRate)}
              testId="solar-tpo-kwh-rate"
            />
          ) : null}
          <TpoStat
            label={TPO_ESCALATOR_LABEL}
            value={formatTpoEscalator(terms.escalatorPercent)}
            testId="solar-tpo-escalator"
          />
          <TpoStat
            label={TPO_TERM_LABEL}
            value={tpoTermText(terms.termYears)}
            testId="solar-tpo-term"
          />
          <TpoStat
            label={TPO_AGREEMENT_LABEL}
            value={tpoAgreementText(terms)}
            testId="solar-tpo-agreement"
          />
          <TpoStat
            label={TPO_PROVIDER_LABEL}
            value={terms.lender}
            testId="solar-tpo-lender"
          />
        </dl>
        {financingSummary.unavailableReason != null ? (
          <p
            role="status"
            className="mt-3 text-[length:var(--step--1)] text-bp-text-muted"
          >
            {financingSummary.unavailableReason}
          </p>
        ) : null}
        {current && payment != null ? (
          <div
            data-testid="solar-tpo-fine-print"
            className="mt-4 flex flex-col gap-2 text-[length:var(--step--1)] text-bp-text-muted"
          >
            <p>
              {lightReachTpoPaymentFinePrint({
                monthlyPayment: formatUsdExact(payment),
                escalatorRate: String(terms.escalatorPercent),
                termYears: terms.termYears,
              })}
            </p>
            <p>{LIGHTREACH_TPO_FINAL_TERMS}</p>
          </div>
        ) : null}
      </div>
      {action}
      <section
        aria-label={TPO_DISCLOSURES_HEADING}
        data-testid="solar-tpo-disclosures"
        className="flex flex-col gap-5 border-t border-bp-border pt-5 text-[length:var(--step--1)] text-bp-text-soft"
      >
        <div className="flex flex-col gap-2">
          <Eyebrow>{TPO_DISCLOSURES_HEADING}</Eyebrow>
          <p>{TPO_DISCLOSURES_INTRO}</p>
          <ol className="flex list-decimal flex-col gap-2 pl-5">
            {TPO_DISCLOSURES.map((disclosure) => (
              <li key={disclosure}>{disclosure}</li>
            ))}
          </ol>
        </div>
        <div className="flex flex-col gap-4">
          <Eyebrow>{TPO_FAQ_HEADING}</Eyebrow>
          {TPO_FAQ.map((entry) => (
            <div key={entry.question} className="flex flex-col gap-1">
              <h4 className="font-semibold text-bp-text">{entry.question}</h4>
              {`answer` in entry ? (
                entry.answer.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))
              ) : (
                <>
                  <h5 className="font-semibold">
                    {TPO_FAQ_LEASE_AND_PPA_LABEL}
                  </h5>
                  {entry.leaseAndPpa.map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                  ))}
                  <h5 className="font-semibold">{TPO_FAQ_LOAN_LABEL}</h5>
                  {entry.loan.map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                  ))}
                </>
              )}
            </div>
          ))}
        </div>
      </section>
    </Panel>
  )
}
